import {
  AfterContentInit,
  ChangeDetectionStrategy,
  Component,
  ContentChild,
  ElementRef,
  inject,
  input,
  output,
  signal,
  TemplateRef,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { ZagButton } from '../button/button';
import { ZagIcon } from '../icon';

/**
 * InlineAssignmentsComponent - Manages a list of items with add/remove functionality
 *
 * Provides a consistent UI for managing lists of assignments (teams, skills, contacts, etc.)
 * with customizable row templates, add/remove actions, and optional custom add forms.
 *
 * @example Basic usage
 * ```html
 * <zag-inline-assignments
 *   [items]="contacts()"
 *   [emptyMessage]="'No contacts'"
 *   [addLabel]="'Add Contact'"
 *   (add)="addContact()"
 *   (remove)="removeContact($event)"
 * >
 *   <ng-template let-contact>
 *     <span>{{ contact.type }}: {{ contact.value }}</span>
 *   </ng-template>
 * </zag-inline-assignments>
 * ```
 *
 * @example With custom add form
 * ```html
 * <zag-inline-assignments [items]="skills()" (remove)="removeSkill($event)">
 *   <ng-template let-skill>
 *     <span>{{ skill.name }}</span>
 *   </ng-template>
 *   <div slot="add-form" class="skill-form">
 *     <zag-dropdown [(ngModel)]="newSkill" [options]="availableSkills" />
 *     <button (click)="addSkill()">Add</button>
 *   </div>
 * </zag-inline-assignments>
 * ```
 *
 * @example With index in template
 * ```html
 * <zag-inline-assignments [items]="items()">
 *   <ng-template let-item let-i="index">
 *     <span>{{ i + 1 }}. {{ item.name }}</span>
 *   </ng-template>
 * </zag-inline-assignments>
 * ```
 */
@Component({
  selector: 'zag-inline-assignments',
  templateUrl: './inline-assignments.html',
  styleUrls: ['./inline-assignments.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgTemplateOutlet, ZagButton, ZagIcon],
})
export class InlineAssignmentsComponent implements AfterContentInit {
  /**
   * Array of items to display
   */
  readonly items = input.required<unknown[]>();

  /**
   * Message to display when items array is empty
   * @default 'No items'
   */
  readonly emptyMessage = input<string>('No items');

  /**
   * Label for the default add button
   * @default 'Add'
   */
  readonly addLabel = input<string>('Add');

  /**
   * Aria label for the remove button
   * @default 'Remove'
   */
  readonly removeLabel = input<string>('Remove');

  /**
   * Aria label for the copy button
   * @default 'Copy'
   */
  readonly copyLabel = input<string>('Copy');

  /**
   * Whether to show the copy button for each row
   * @default false
   */
  readonly showCopy = input<boolean>(false);

  /**
   * TrackBy function for efficient list rendering
   * @default Uses index
   */
  readonly trackBy = input<(item: unknown, index: number) => unknown>((_: unknown, index: number) => index);

  readonly loading = input<boolean>(false);
  readonly disabled = input<boolean>(false);

  /**
   * Emitted when the add button is clicked
   */
  readonly add = output<void>();

  /**
   * Emitted when a remove button is clicked
   * Emits the index of the item to remove
   */
  readonly remove = output<number>();

  /**
   * Emitted when a copy button is clicked
   * Emits the index of the item to copy
   */
  readonly copyItem = output<number>();

  /**
   * Template for rendering individual items
   * Provided via ng-template with implicit context
   */
  @ContentChild(TemplateRef) itemTemplate!: TemplateRef<{
    $implicit: unknown;
    index: number;
  }>;

  /**
   * Whether a custom add form is provided
   * When true, the default add button is hidden
   */
  readonly hasAddForm = signal(false);

  private readonly el = inject(ElementRef);

  ngAfterContentInit(): void {
    // Check if custom add form slot is provided
    const addFormSlot = this.el.nativeElement.querySelector('[slot="add-form"]');
    this.hasAddForm.set(!!addFormSlot);
  }
}
